import React, { useState } from 'react';
import { getAlternativeImages } from '../services/image-service';
import LoadingSpinner from './LoadingSpinner';
import './SceneCard.css';

const SceneCard = ({ scene, sceneIndex, onRegenerateScene }) => {
  const [loading, setLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageOptions, setImageOptions] = useState([]);
  const [showImageOptions, setShowImageOptions] = useState(false);

  // Fallback image if the main one fails to load
  const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1518020382113-a7e8fc38eac9?q=80&w=1000&auto=format&fit=crop';

  // Handle image error
  const handleImageError = () => {
    setImageError(true);
  };

  // Fetch alternative images for this scene
  const handleFetchAlternativeImages = async () => {
    if (!scene.imageKeywords) return;
    
    try {
      setLoading(true);
      const images = await getAlternativeImages(scene.imageKeywords, scene.imageUrl);
      setImageOptions(images);
      setShowImageOptions(true);
    } catch (error) {
      console.error('[SceneCard] Error fetching alternative images:', error);
    } finally {
      setLoading(false);
    }
  };

  // Select a new image
  const handleSelectImage = (imageUrl) => {
    // Update local scene state with the new image URL
    scene.imageUrl = imageUrl;
    setImageOptions([]);
    setShowImageOptions(false);
    setImageError(false);
  };

  // Get the current image URL (either the scene's image or fallback)
  const currentImageUrl = imageError ? FALLBACK_IMAGE : scene.imageUrl;

  return (
    <div className="scene-card">
      {loading && <LoadingSpinner overlay={true} message="Finding new images..." />}
      
      <div className="scene-image-container">
        <img 
          src={currentImageUrl} 
          alt={scene.imageKeywords || "Story scene"} 
          className="scene-image"
          onError={handleImageError}
        />
        
        <div className="scene-image-actions">
          {onRegenerateScene && (
            <button 
              onClick={onRegenerateScene} 
              className="scene-action regenerate"
              aria-label="Regenerate this scene"
            >
              <span aria-hidden="true">📝</span>
            </button>
          )}
          
          {scene.imageKeywords && (
            <button 
              onClick={handleFetchAlternativeImages} 
              className="scene-action refresh"
              aria-label="Get different images"
            >
              <span aria-hidden="true">🔄</span>
            </button>
          )}
        </div>
        
        {/* Image options carousel */}
        {showImageOptions && imageOptions.length > 0 && (
          <div className="image-options-carousel">
            {imageOptions.map((imageUrl, idx) => (
              <div 
                key={idx} 
                className="image-option"
                onClick={() => handleSelectImage(imageUrl)}
              >
                <img 
                  src={imageUrl} 
                  alt={`Alternative ${idx + 1}`} 
                  className="image-option-thumbnail"
                />
              </div>
            ))}
          </div>
        )}
      </div>
      
      <div className="scene-text">
        <p>{scene.text}</p>
      </div>
      
      {sceneIndex !== undefined && (
        <div className="scene-index">Scene {sceneIndex + 1}</div>
      )}
    </div>
  );
};

export default SceneCard;
